import React, { useEffect, useRef, useState } from "react";
import InterviewChart from "./InterviewChart";
import { Segmented, Space } from "antd";
import InterviewPieChart from "./InterviewPieChart";
import FruadSSDisplay from "./FruadSSDisplay";
import Icon from "../../../assets/Icon";
import AnswerAnalysis from "./AnswerAnalysis";
import MonitorMain from "./monitorAnalysis/MonitorMain";
import AudioAnomaly from "./AudioAnomaly";
import MBTIAnalysis from "./personalityAnalysis/MBTIAnalysis";

function InterviewOrientation({
  config,
  analysisData,
  updateVideoTrack,
  interviewAnalysisIntegrityData,
  jdLevelData,
  windowsSusAppData,
  macSusAppData,
  mbtiAnalysisData,
  tab,
}) {
  const tabRef = useRef(null);

  const [dataPoints, setDataPoints] = useState(null);
  const [dataPointsUpDown, setDataPointsUpDown] = useState(null);
  const [dataPointsPupil, setDataPointsPupil] = useState(null);
  const [dataPointsDominant, setDataPointsDominant] = useState(null);
  const [orientation, setOrientation] = useState("Fraud Analysis");

  const [minLookingLeft, setMinLookingLeft] = useState(null);
  const [maxLookingLeft, setMaxLookingLeft] = useState(null);

  const [minLookingDown, setMinLookingDown] = useState(null);
  const [maxLookingDown, setMaxLookingDown] = useState(null);

  const [lookingDownStraight, setLookingDownStraight] = useState({
    minValue: null,
    maxValue: null,
  });
  const [lookingLeftRightStraight, setLookingLeftRightStraight] = useState({
    minValue: null,
    maxValue: null,
  });

  const [lookingUp, setLookingUp] = useState({
    minValue: null,
    maxValue: null,
  });
  const [lookingRight, setLookingRight] = useState({
    minValue: null,
    maxValue: null,
  });

  useEffect(() => {
    // Simulating dynamic data updates
    if (analysisData) {
      // console.log(analysisData);
      const dataPoints = Object.entries(analysisData)?.map(([time, data]) => ({
        time: parseFloat(time),
        value: data.headRightLeftOrientation.value,
        label: data.headRightLeftOrientation.analysis,
      }));
      const dataPointsUpDown = Object.entries(analysisData)?.map(
        ([time, data]) => ({
          time: parseFloat(time),
          value: data.headUpDownOrientation.value,
          label: data.headUpDownOrientation.analysis,
        }),
      );

      const dataPointsPupil = Object.entries(analysisData)?.map(
        ([time, data]) => ({
          time: parseFloat(time),
          value: data.pupilOrientation.value,
          label: data.pupilOrientation.analysis,
        }),
      );
      const dataPointsDominant = Object.entries(analysisData)?.map(
        ([time, data]) => ({
          // time: parseFloat(time),
          value: data.dominantEmotion.value,
          label: data.dominantEmotion.analysis,
        }),
      );

      // console.log(dataPoints)
      setDataPoints(dataPoints);
      setDataPointsUpDown(dataPointsUpDown);
      setDataPointsPupil(dataPointsPupil);
      setDataPointsDominant(dataPointsDominant);
    }
    if (tab) {
      tabRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      if (tab === "fraud") {
        setOrientation("Fraud Analysis");
      } else if (tab === "personality") {
        setOrientation("Personality Analysis");
      }
    }
  }, [analysisData]);

  useEffect(() => {
    if (dataPointsPupil) {
      // setPupilDirection
      const pupilLeft = dataPointsPupil.filter(
        (point) => point.label === "Left",
      );
      const pupilRight = dataPointsPupil.filter(
        (point) => point.label === "Right",
      );
      const pupilCenter = dataPointsPupil.filter(
        (point) => point.label === "Center",
      );
    }
  }, [dataPointsPupil]);

  useEffect(() => {
    if (dataPoints && dataPointsUpDown) {
      const lookingLeftPoints = dataPoints.filter(
        (point) => point.label === "looking left",
      );
      const lookingDownPoints = dataPointsUpDown.filter(
        (point) => point.label === "looking down",
      );

      const lookingUpDownStraightPoints = dataPointsUpDown.filter(
        (point) => point.label === "looking straight",
      );
      const lookingLeftRightStraightPoints = dataPoints.filter(
        (point) => point.label === "looking straight",
      );

      const lookingRightPoints = dataPoints.filter(
        (point) => point.label === "looking right",
      );
      const lookingUpPoints = dataPointsUpDown.filter(
        (point) => point.label === "looking up",
      );

      if (lookingLeftPoints.length > 0) {
        const min = Math.min(...lookingLeftPoints.map((point) => point.value));
        const max = Math.max(...lookingLeftPoints.map((point) => point.value));

        setMinLookingLeft(min);
        setMaxLookingLeft(max);
      } else {
        setMinLookingLeft(null);
        setMaxLookingLeft(null);
      }
      if (lookingDownPoints.length > 0) {
        const min = Math.min(...lookingDownPoints.map((point) => point.value));
        const max = Math.max(...lookingDownPoints.map((point) => point.value));

        setMinLookingDown(min);
        setMaxLookingDown(max);
      } else {
        setMinLookingDown(null);
        setMaxLookingDown(null);
      }
      if (lookingLeftRightStraightPoints.length > 0) {
        const min = Math.min(
          ...lookingLeftRightStraightPoints.map((point) => point.value),
        );
        const max = Math.max(
          ...lookingLeftRightStraightPoints.map((point) => point.value),
        );

        setLookingLeftRightStraight((prev) => ({
          ...prev,
          minValue: min,
          maxValue: max,
        }));
      } else {
        setLookingLeftRightStraight({
          minValue: null,
          maxValue: null,
        });
      }
      if (lookingUpDownStraightPoints.length > 0) {
        const min = Math.min(
          ...lookingUpDownStraightPoints.map((point) => point.value),
        );
        const max = Math.max(
          ...lookingUpDownStraightPoints.map((point) => point.value),
        );

        setLookingDownStraight((prev) => ({
          ...prev,
          minValue: min,
          maxValue: max,
        }));
      } else {
        setLookingDownStraight({
          minValue: null,
          maxValue: null,
        });
      }
      if (lookingRightPoints.length > 0) {
        const min = Math.min(...lookingRightPoints.map((point) => point.value));
        const max = Math.max(...lookingRightPoints.map((point) => point.value));

        setLookingRight((prev) => ({
          ...prev,
          minValue: min,
          maxValue: max,
        }));
      } else {
        setLookingRight({
          minValue: null,
          maxValue: null,
        });
      }
      if (lookingUpPoints.length > 0) {
        const min = Math.min(...lookingUpPoints.map((point) => point.value));
        const max = Math.max(...lookingUpPoints.map((point) => point.value));

        setLookingUp((prev) => ({
          ...prev,
          minValue: min,
          maxValue: max,
        }));
      } else {
        setLookingUp({
          minValue: null,
          maxValue: null,
        });
      }
    }
  }, [dataPoints, dataPointsUpDown]);

  // console.log(minLookingLeft, maxLookingLeft);

  const removeTab = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("tab");
    window.history.replaceState({}, "", url);
  };

  return (
    <div className="mt-10">
      <div ref={tabRef} className="analysis-segment w-full overflow-x-scroll scroll">
        <div className="inline-flex min-w-max">
          <Segmented
            options={[
              "Fraud Analysis",
              "Answer Analysis",
              "Head Orientation",
              "Personality Analysis",
            ]}
            value={orientation}
            onChange={(value) => {
              setOrientation(value);
              removeTab();
            }}
            size="large"
          />
        </div>
      </div>
      {interviewAnalysisIntegrityData && orientation === "Fraud Analysis" && (
        <>
          <FruadSSDisplay
            config={config}
            interviewAnalysisIntegrityData={interviewAnalysisIntegrityData}
            updateVideoTrack={updateVideoTrack}
          />
          <MonitorMain
            monitorData={
              interviewAnalysisIntegrityData?.interviewEnvironmentMonitoringDataList
            }
            updateVideoTrack={updateVideoTrack}
            windowsSusAppData={windowsSusAppData}
            macSusAppData={macSusAppData}
          />
          <AudioAnomaly
            interviewAnalysisIntegrityData={interviewAnalysisIntegrityData}
            updateVideoTrack={updateVideoTrack}
          />
        </>
      )}
      {orientation === "Answer Analysis" ? (
        <div className="mt-6">
          <AnswerAnalysis
            interviewAnalysisIntegrityData={interviewAnalysisIntegrityData}
            updateVideoTrack={updateVideoTrack}
            jdLevelData={jdLevelData}
          />
        </div>
      ) : (
        <></>
      )}
      {orientation === "Head Orientation" ? (
        config?.videoRecordingRequired ? (
          <div className="mt-6">
            <div className="flex md:flex-row flex-col gap-2 w-full items-start md:justify-between pl-1 pr-3 mb-3">
              <h2 className="font-semibold text-[1.1rem] border-l-[7px] border-l-[#2888f6] px-3 py-1 text-start">
                Head Orientation (Left/Right)
              </h2>
              <Space
                className="looking-container text-[.88rem] flex flex-wrap"
                direction="horizontal"
                size={10}
              >
                {/* bg-[#F790094D] */}
                <p className="py-[.35rem] px-[1.175rem] rounded-2xl text-white bg-[#FF000099] text-center">
                  Anomaly
                </p>
                <p className="py-[.35rem] px-[1.175rem] rounded-2xl text-[black] bg-[#F790094D] text-center">
                  Looking Right
                </p>
                <p className="py-[.35rem] px-[1.175rem] rounded-2xl text-[black] bg-[#2888F64D] text-center">
                  Looking Straight
                </p>

                <p className="py-[.35rem] px-[1.175rem] rounded-2xl text-[black] bg-[#F044384D] text-center">
                  Looking Left
                </p>
              </Space>
            </div>
            <InterviewChart
              updateVideoTrack={updateVideoTrack}
              dataPoints={dataPoints}
              chartTitle="Head orientaion Left/Right"
              minRedColor={minLookingLeft}
              maxRedColor={maxLookingLeft}
              blueColor={lookingLeftRightStraight}
              orangeColor={lookingRight}
              chartLeftTitleNegative="left"
              chartLeftTitlePositive="right"
            />
            <div className="mt-6">
              <div className="flex md:flex-row flex-col gap-2 w-full items-start md:justify-between pl-1 pr-3 mb-3">
                <h2 className="font-semibold text-[1.1rem] border-l-[7px] border-l-[#2888f6] px-3 py-1">
                  Head Orientation (Up/Down)
                </h2>
                <Space
                  className="looking-container text-[.88rem] flex flex-wrap"
                  direction="horizontal"
                  size={10}
                >
                  <p className="py-[.35rem] px-[1.175rem] rounded-2xl text-white bg-[#FF000099] text-center">
                    Anomaly
                  </p>
                  <p className="py-[.35rem] px-[1.175rem] rounded-2xl text-[black] bg-[#F790094D] text-center">
                    Looking Up
                  </p>
                  <p className="py-[.35rem] px-[1.175rem] rounded-2xl text-[black] bg-[#2888F64D] text-center">
                    Looking Straight
                  </p>

                  <p className="py-[.35rem] px-[1.175rem] rounded-2xl text-[black] bg-[#F044384D] text-center">
                    Looking Down
                  </p>
                </Space>
              </div>

              <InterviewChart
                updateVideoTrack={updateVideoTrack}
                dataPoints={dataPointsUpDown}
                chartTitle="Head orientaion Up/Down"
                minRedColor={minLookingDown}
                maxRedColor={maxLookingDown}
                blueColor={lookingDownStraight}
                orangeColor={lookingUp}
                chartLeftTitleNegative="down"
                chartLeftTitlePositive="up"
              />
            </div>
            <div className="mt-5 max-w-[88rem] mx-auto bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
              <strong>Note:</strong> The two graph above shows the candidate’s
              head orientation during the interview (looking left, right, up,
              down, straight, or anomaly). Maintaining a straight face toward
              the screen is considered normal, while frequent deviations or
              anomalies may affect the integrity.
            </div>
          </div>
        ) : (
          <div className="mt-8">
            <p className="text-center flex justify-center">
              <Icon type="lock-orientation-icon" width="46" height="46" />
            </p>
            <p className=" text-[#475467] text-center mt-2">
              Upgrade/Enable to unlock head orientation features.
            </p>
          </div>
        )
      ) : (
        <></>
      )}
      {orientation === "Personality Analysis" ? (
        config?.videoRecordingRequired ? (
          <div className="mt-5 flex flex-col gap-12">
            {/* <h2 className="font-semibold ">Pupil Orientation</h2> */}
            <InterviewPieChart dataPoints={dataPointsDominant} />
            <MBTIAnalysis data={mbtiAnalysisData} />
          </div>
        ) : (
          <div className="mt-8">
            <p className="text-center flex justify-center">
              <Icon type="lock-orientation-icon" width="46" height="46" />
            </p>
            <p className=" text-[#475467] text-center mt-2">
              Upgrade/Enable to unlock emotion analysis features.
            </p>
          </div>
        )
      ) : (
        <></>
      )}
    </div>
  );
}

export default InterviewOrientation;
